import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { balanceReceiptsAPI } from '../../lib/api';
import { receiptUrl } from '../../components/admin/BankTransferReceiptFields';
import { fetchBankSender } from '../../components/admin/BankSenderSettings';
import { WithdrawalProcessAdminPanel } from '../../components/admin/WithdrawalProcessAdminPanel';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Receipt, Loader2, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';

const TABS = [
    { key: 'pending', label: 'Pendientes', Icon: Clock, cls: 'text-amber-400' },
    { key: 'approved', label: 'Aprobados', Icon: CheckCircle, cls: 'text-emerald-400' },
    { key: 'rejected', label: 'Rechazados', Icon: XCircle, cls: 'text-red-400' },
];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtAmount = (a, c) => `${c === 'USD' ? '$' : '€'}${Number(a || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

const AdminBalanceReceiptsPage = () => {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [tab, setTab] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [review, setReview] = useState(null);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [sender, setSender] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await balanceReceiptsAPI.adminList();
            setItems(data.requests || []);
            setCounts(data.counts || {});
            fetchBankSender().then((d) => setSender(d.name)).catch(() => {});
        } catch {
            toast.error('Error al cargar comprobantes');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const confirm = async () => {
        if (review.action === 'reject' && reason.trim().length < 5) { toast.error('Indica el motivo del rechazo'); return; }
        setSubmitting(true);
        try {
            if (review.action === 'approve') {
                const { data } = await balanceReceiptsAPI.adminApprove(review.req.id);
                toast.success(`Saldo agregado · nuevo saldo ${fmtAmount(data.new_balance, review.req.currency)}`);
            } else {
                await balanceReceiptsAPI.adminReject(review.req.id, reason.trim());
                toast.success('Comprobante rechazado y usuario notificado');
            }
            setReview(null); setReason('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al registrar la revisión');
        } finally {
            setSubmitting(false);
        }
    };

    const visible = items.filter((r) => r.status === tab);

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-balance-receipts-page">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-[#1973B8]/15 border border-[#1973B8]/30">
                        <Receipt className="w-6 h-6 text-[#7CB1E5]" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Comprobantes de Abono</h1>
                        <p className="text-sm text-slate-400 font-light">Comprobantes enviados por los usuarios · Aprobar agrega el saldo automáticamente</p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold transition-colors ${tab === t.key ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:text-white'}`}
                            data-testid={`balance-receipts-tab-${t.key}`}>
                            <t.Icon className={`w-4 h-4 ${t.cls}`} /> {t.label}
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300">{counts[t.key] || 0}</span>
                        </button>
                    ))}
                </div>

                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-[#7CB1E5] animate-spin" /></div>
                    ) : visible.length === 0 ? (
                        <p className="text-slate-600 text-sm text-center py-12" data-testid="balance-receipts-empty">Sin comprobantes en esta categoría.</p>
                    ) : (
                        <div className="divide-y divide-slate-800/60">
                            {visible.map((r) => (
                                <div key={r.id} className="px-4 py-3.5 flex flex-wrap items-center gap-3" data-testid={`admin-receipt-${r.id}`}>
                                    <div className="flex-1 min-w-[220px]">
                                        <p className="text-white text-sm font-semibold">{fmtAmount(r.amount, r.currency)} · {r.user_name}</p>
                                        <p className="text-slate-500 text-xs">{r.user_email} · {r.filename} · {fmtDate(r.created_at)}</p>
                                        {r.note && <p className="text-slate-400 text-xs mt-0.5">Nota del usuario: {r.note}</p>}
                                        {r.reject_reason && <p className="text-red-300 text-xs mt-0.5">Motivo: {r.reject_reason}</p>}
                                        {r.reviewed_by && <p className="text-slate-600 text-[10.5px] mt-0.5">Revisado por {r.reviewed_by} · {fmtDate(r.reviewed_at)}</p>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <a href={receiptUrl(r.file_id)} target="_blank" rel="noreferrer"
                                            className="inline-flex items-center h-8 px-3 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs font-semibold"
                                            data-testid={`admin-receipt-view-${r.id}`}>
                                            <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                                        </a>
                                        {r.status === 'pending' && (
                                            <>
                                                <Button size="sm" onClick={() => { setReview({ req: r, action: 'approve' }); setReason(''); }}
                                                    className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 h-8"
                                                    data-testid={`admin-receipt-approve-${r.id}`}>
                                                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Aprobar y agregar saldo
                                                </Button>
                                                <Button size="sm" onClick={() => { setReview({ req: r, action: 'reject' }); setReason(''); }}
                                                    className="bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 h-8"
                                                    data-testid={`admin-receipt-reject-${r.id}`}>
                                                    <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border-t border-slate-800 pt-6">
                    <WithdrawalProcessAdminPanel />
                </div>

                <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
                    <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white" data-testid="balance-receipt-review-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-white">{review?.action === 'approve' ? 'Aprobar y agregar saldo' : 'Rechazar comprobante'}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 py-1">
                            <p className="text-slate-400 text-sm">{review?.req?.user_name} · <span className="text-white font-semibold">{review && fmtAmount(review.req.amount, review.req.currency)}</span></p>
                            {review?.action === 'approve' ? (
                                <p className="text-xs text-slate-400 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                                    Se agregará el saldo con el concepto <span className="text-white">TRANSFERENCIA BANCARIA A FAVOR DE: {review.req.user_name?.toUpperCase()}</span> · Remitente: <span className="text-white" data-testid="approve-dialog-sender">{sender || '…'}</span>, con este comprobante vinculado.
                                </p>
                            ) : (
                                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del rechazo (obligatorio)"
                                    className="bg-slate-950 border-slate-800 text-white text-sm min-h-[90px]" data-testid="balance-receipt-reason-input" />
                            )}
                        </div>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setReview(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancelar</Button>
                            <Button onClick={confirm} disabled={submitting}
                                className={review?.action === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}
                                data-testid="balance-receipt-confirm-btn">
                                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                                Confirmar
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default AdminBalanceReceiptsPage;
